/**
 * Featured Content API Client
 * Handles featured content placements for listings, developments, posts, and projects
 * 
 * The Featured Content system allows entities to be marked as "featured" across channels,
 * enabling prominent display on Royale websites (hero carousels, spotlight sections, etc.)
 */

import { BaseAPIClient } from '../base-client';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Placement types for featured content
 * 
 * These values come directly from the Route Royal API.
 * Updated: Jan 2026 with comprehensive Route Royal enums (67 placement types)
 * 
 * @see Route Royal Migration: 20260107053702_add_featured_enums
 */
export type FeaturedPlacementType =
  // =========================================================================
  // Primary/Hero (4)
  // =========================================================================
  | 'HERO'                // Hero banner (primary)
  | 'HERO_CAROUSEL'       // Hero carousel
  | 'HERO_VIDEO'          // Hero with video background
  | 'HERO_FULLSCREEN'     // Fullscreen hero
  
  // =========================================================================
  // Homepage (5)
  // =========================================================================
  | 'HOMEPAGE_SLIDER'     // Homepage slider/carousel
  | 'HOMEPAGE_GRID'       // Homepage featured grid
  | 'HOMEPAGE_SPOTLIGHT'  // Homepage spotlight section
  | 'HOMEPAGE_SHOWCASE'   // Homepage showcase
  | 'HOMEPAGE_BANNER'     // Homepage banner
  
  // =========================================================================
  // Category/Collection (5)
  // =========================================================================
  | 'CATEGORY_HERO'       // Category page hero
  | 'CATEGORY_FEATURED'   // Category featured listings
  | 'CATEGORY_HIGHLIGHT'  // Category highlight
  | 'SECTION_SPOTLIGHT'   // Section spotlight
  | 'COLLECTION_FEATURED' // Featured in collection
  
  // =========================================================================
  // Search & Discovery (5)
  // =========================================================================
  | 'SEARCH_BOOST'        // Boosted in search results
  | 'SEARCH_PINNED'       // Pinned at top of search
  | 'SEARCH_HIGHLIGHT'    // Highlighted in search
  | 'MAP_FEATURED'        // Featured on map view
  | 'MAP_PINNED'          // Pinned on map
  
  // =========================================================================
  // Sidebar & Widgets (5)
  // =========================================================================
  | 'SIDEBAR_FEATURED'    // Sidebar featured
  | 'SIDEBAR_RECENT'      // Sidebar recent
  | 'SIDEBAR_PREMIUM'     // Sidebar premium
  | 'WIDGET_FEATURED'     // Widget featured
  | 'WIDGET_TRENDING'     // Widget trending
  
  // =========================================================================
  // Team/Agent (5)
  // =========================================================================
  | 'AGENT_SPOTLIGHT'     // Agent spotlight
  | 'AGENT_HERO'          // Agent hero
  | 'TEAM_HERO'           // Team hero
  | 'TEAM_FEATURED'       // Team featured
  | 'OFFICE_SPOTLIGHT'    // Office spotlight
  
  // =========================================================================
  // Testimonials (4)
  // =========================================================================
  | 'TESTIMONIAL_HERO'    // Testimonial hero
  | 'TESTIMONIAL_SLIDER'  // Testimonial slider
  | 'TESTIMONIAL_FEATURED'// Testimonial featured
  | 'REVIEW_HIGHLIGHT'    // Review highlight
  
  // =========================================================================
  // Property-Specific (10)
  // =========================================================================
  | 'AUCTION_SPOTLIGHT'   // Auction spotlight
  | 'AUCTION_HERO'        // Auction hero
  | 'OPEN_HOME_FEATURED'  // Open home featured
  | 'OPEN_HOME_BANNER'    // Open home banner
  | 'NEW_LISTING_SPOTLIGHT'// New listing spotlight
  | 'PRICE_REDUCED_BANNER'// Price reduced banner
  | 'SOLD_SHOWCASE'       // Sold showcase
  | 'RENTED_SHOWCASE'     // Rented showcase
  | 'COMING_SOON_FEATURED'// Coming soon featured
  | 'OFF_MARKET_FEATURED' // Off market featured
  
  // =========================================================================
  // Development/Project (5)
  // =========================================================================
  | 'PROJECT_HERO'        // Project hero
  | 'PROJECT_FEATURED'    // Project featured
  | 'PROJECT_SHOWCASE'    // Project showcase
  | 'LAND_FEATURED'       // Land featured
  | 'COMMUNITY_SPOTLIGHT' // Community spotlight
  
  // =========================================================================
  // Marketing Channels (8)
  // =========================================================================
  | 'NEWSLETTER_FEATURED' // Newsletter featured
  | 'NEWSLETTER_HERO'     // Newsletter hero
  | 'NEWSLETTER_SPOTLIGHT'// Newsletter spotlight
  | 'SOCIAL_FEATURED'     // Social media featured
  | 'SOCIAL_SPOTLIGHT'    // Social media spotlight
  | 'EMAIL_FEATURED'      // Email campaign featured
  | 'SMS_FEATURED'        // SMS campaign featured
  
  // =========================================================================
  // Page-Specific (6)
  // =========================================================================
  | 'FOOTER_FEATURED'     // Footer featured
  | 'CONTACT_PAGE_FEATURED'// Contact page featured
  | 'ABOUT_PAGE_FEATURED' // About page featured
  | 'SERVICES_PAGE_FEATURED'// Services page featured
  | 'LANDING_PAGE_HERO'   // Landing page hero
  | 'LANDING_PAGE_FEATURED'// Landing page featured
  
  // =========================================================================
  // Portal/Syndication (4)
  // =========================================================================
  | 'PORTAL_FEATURED'     // Portal featured
  | 'PORTAL_PREMIUM'      // Portal premium
  | 'PORTAL_BOOST'        // Portal boost
  | 'PORTAL_SPOTLIGHT'    // Portal spotlight
  
  // =========================================================================
  // Lifestyle/Specialty (7)
  // =========================================================================
  | 'LUXURY_SHOWCASE'     // Luxury showcase
  | 'INVESTMENT_SPOTLIGHT'// Investment spotlight
  | 'LIFESTYLE_FEATURED'  // Lifestyle featured
  | 'RURAL_FEATURED'      // Rural featured
  | 'COMMERCIAL_SPOTLIGHT'// Commercial spotlight
  | 'RETIREMENT_FEATURED' // Retirement featured
  | 'FIRST_HOME_SPOTLIGHT'// First home spotlight
  
  // =========================================================================
  // Legacy compatibility
  // =========================================================================
  | 'NEWSLETTER_FEATURE'  // Legacy: use NEWSLETTER_FEATURED
  | 'PREMIUM_LISTING'     // Legacy: use badge PREMIUM instead
  | 'NEW_LISTING'         // Legacy: use badge NEW or JUST_LISTED
  | 'PRICE_REDUCED'       // Legacy: use badge REDUCED or PRICE_DROP
  | 'OPEN_HOME';          // Legacy: use OPEN_HOME_FEATURED

/**
 * Badge variants for visual differentiation
 */
export type FeaturedBadgeVariant =
  | 'FEATURED'        // Generic featured badge
  | 'HOT'             // Hot property badge
  | 'NEW'             // New listing badge
  | 'EXCLUSIVE'       // Exclusive listing
  | 'PREMIUM'         // Premium property
  | 'PRICE_REDUCED'   // Price reduced
  | 'MUST_SEE'        // Must see property
  | 'INVESTMENT'      // Investment opportunity
  | 'LUXURY'          // Luxury property
  | 'ECO_FRIENDLY';   // Eco-friendly/sustainable

/**
 * Status of a featured placement
 */
export type FeaturedPlacementStatus =
  | 'SCHEDULED'
  | 'ACTIVE'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'PAUSED';

/**
 * Entity types that can be featured
 */
export type FeaturedEntityType = 'LISTING' | 'DEVELOPMENT' | 'POST' | 'PROJECT';

/**
 * A featured placement record
 */
export interface FeaturedPlacement {
  id: string;
  entityType: FeaturedEntityType;
  entityId: string;
  channelId: string;
  placementType: FeaturedPlacementType;
  priority: number;
  status: FeaturedPlacementStatus;
  startDate: string | null;
  endDate: string | null;
  label: string | null;
  badgeVariant: FeaturedBadgeVariant | null;
  impressions: number;
  clicks: number;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Simplified featured placement for card display
 */
export interface FeaturedPlacementSummary {
  placementType: FeaturedPlacementType;
  label: string | null;
  badgeVariant: FeaturedBadgeVariant | null;
  priority: number;
}

/**
 * Response from fetching featured placements for a channel
 */
export interface FeaturedContentResponse {
  data: FeaturedPlacement[];
  meta: {
    total: number;
    page: number;
    limit: number;
  };
}

/**
 * Options for fetching featured content
 */
export interface GetFeaturedOptions {
  entityType?: FeaturedEntityType;
  placementType?: FeaturedPlacementType;
  status?: FeaturedPlacementStatus;
  limit?: number;
  page?: number;
}

// =============================================================================
// API CLIENT
// =============================================================================

export class FeaturedContentAPIClient extends BaseAPIClient {
  /**
   * Get featured placements for a channel
   * 
   * @param organisationId - The organisation ID
   * @param channelId - The channel/site integration ID
   * @param options - Filter options
   */
  async getFeaturedForChannel(
    organisationId: string,
    channelId: string,
    options?: GetFeaturedOptions
  ): Promise<FeaturedContentResponse> {
    const params = new URLSearchParams();
    params.append('status', options?.status || 'ACTIVE');
    
    if (options?.entityType) params.append('entityType', options.entityType);
    if (options?.placementType) params.append('placementType', options.placementType);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.page) params.append('page', options.page.toString());

    const raw = await this.request<FeaturedContentResponse | FeaturedPlacement[]>(
      `/organisations/${organisationId}/channels/${channelId}/featured?${params.toString()}`
    );

    // The Route Royal API wraps all responses in { success, data, error }.
    // The base client's envelope unwrapper strips this, so for the featured
    // endpoint (which returns a plain array in `data`) we receive the raw
    // FeaturedPlacement[] directly. Normalise to the expected paginated shape.
    if (Array.isArray(raw)) {
      return { data: raw, meta: { total: raw.length, page: 1, limit: options?.limit ?? 50 } };
    }
    if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray(raw.data)) {
      return raw as FeaturedContentResponse;
    }
    return { data: [], meta: { total: 0, page: 1, limit: options?.limit ?? 50 } };
  }

  /**
   * Get featured placements for a specific entity
   * 
   * @param organisationId - The organisation ID
   * @param entityType - The entity type (LISTING, DEVELOPMENT, POST, PROJECT)
   * @param entityId - The entity ID
   */
  async getEntityFeaturedPlacements(
    organisationId: string,
    entityType: FeaturedEntityType,
    entityId: string
  ): Promise<FeaturedPlacement[]> {
    const raw = await this.request<{ data: FeaturedPlacement[] } | FeaturedPlacement[]>(
      `/organisations/${organisationId}/featured/entity/${entityType}/${entityId}`
    );
    if (Array.isArray(raw)) return raw;
    return raw?.data || [];
  }

  /**
   * Check if an entity is featured (convenience method)
   * 
   * @param organisationId - The organisation ID
   * @param entityType - The entity type
   * @param entityId - The entity ID
   */
  async isEntityFeatured(
    organisationId: string,
    entityType: FeaturedEntityType,
    entityId: string
  ): Promise<boolean> {
    try {
      const placements = await this.getEntityFeaturedPlacements(
        organisationId,
        entityType,
        entityId
      );
      return placements.some(p => p.status === 'ACTIVE');
    } catch {
      return false;
    }
  }

  /**
   * Get featured listings for a specific placement type
   * 
   * @param organisationId - The organisation ID
   * @param channelId - The channel/site integration ID
   * @param placementType - The placement type to filter by
   * @param limit - Maximum number of results
   */
  async getFeaturedListingsForPlacement(
    organisationId: string,
    channelId: string,
    placementType: FeaturedPlacementType,
    limit: number = 10
  ): Promise<FeaturedPlacement[]> {
    const response = await this.getFeaturedForChannel(
      organisationId,
      channelId,
      {
        entityType: 'LISTING',
        placementType,
        limit,
      }
    );
    
    // Sort by priority (higher priority first)
    return response.data.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Track an impression for a featured placement
   * 
   * @param placementId - The featured placement ID
   */
  async trackImpression(placementId: string): Promise<void> {
    await this.request(
      `/featured/${placementId}/impression`,
      { method: 'POST' }
    );
  }

  /**
   * Track a click for a featured placement
   * 
   * @param placementId - The featured placement ID
   */
  async trackClick(placementId: string): Promise<void> {
    await this.request(
      `/featured/${placementId}/click`,
      { method: 'POST' }
    );
  }
}

// Export singleton instance
export const featuredContentAPI = new FeaturedContentAPIClient();

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get the primary featured placement from an array of placements
 * Returns the highest priority active placement with a badge variant
 */
export function getPrimaryFeaturedPlacement(
  placements: FeaturedPlacement[] | FeaturedPlacementSummary[]
): FeaturedPlacement | FeaturedPlacementSummary | null {
  if (!placements || placements.length === 0) return null;
  
  // Filter for placements with badge variants and sort by priority
  const withBadges = placements
    .filter(p => p.badgeVariant)
    .sort((a, b) => b.priority - a.priority);
  
  return withBadges[0] || null;
}

/**
 * Check if an entity has any active featured placements
 */
export function isEntityFeatured(
  placements: FeaturedPlacement[] | FeaturedPlacementSummary[]
): boolean {
  return placements && placements.length > 0;
}

/**
 * Create a map of entity IDs to their featured placements
 * Useful for enriching listings with featured data
 */
export function createFeaturedPlacementsMap(
  placements: FeaturedPlacement[]
): Map<string, FeaturedPlacement[]> {
  const map = new Map<string, FeaturedPlacement[]>();
  
  for (const placement of placements) {
    const existing = map.get(placement.entityId) || [];
    existing.push(placement);
    map.set(placement.entityId, existing);
  }
  
  return map;
}

/**
 * Convert FeaturedPlacement to FeaturedPlacementSummary for card display
 */
export function toFeaturedPlacementSummary(
  placement: FeaturedPlacement
): FeaturedPlacementSummary {
  return {
    placementType: placement.placementType,
    label: placement.label,
    badgeVariant: placement.badgeVariant,
    priority: placement.priority,
  };
}

// =============================================================================
// PLACEMENT TYPE CATEGORIES
// =============================================================================

/**
 * Placement types that should appear in the Hero section
 */
export const HERO_PLACEMENT_TYPES: Set<FeaturedPlacementType> = new Set([
  'HERO',
  'HERO_CAROUSEL',
  'HERO_VIDEO',
  'HERO_FULLSCREEN',
]);

/**
 * Placement types that should appear in the Homepage Featured section
 * (Featured Properties carousel, Homepage Grid, etc.)
 */
export const HOMEPAGE_PLACEMENT_TYPES: Set<FeaturedPlacementType> = new Set([
  'HOMEPAGE_SLIDER',
  'HOMEPAGE_GRID',
  'HOMEPAGE_SPOTLIGHT',
  'HOMEPAGE_SHOWCASE',
  'HOMEPAGE_BANNER',
]);

/**
 * Placement types that should appear in Category sections
 */
export const CATEGORY_PLACEMENT_TYPES: Set<FeaturedPlacementType> = new Set([
  'CATEGORY_HERO',
  'CATEGORY_FEATURED',
  'CATEGORY_HIGHLIGHT',
  'SECTION_SPOTLIGHT',
  'COLLECTION_FEATURED',
]);

/**
 * Placement types for search result boosting/pinning
 */
export const SEARCH_PLACEMENT_TYPES: Set<FeaturedPlacementType> = new Set([
  'SEARCH_BOOST',
  'SEARCH_PINNED',
  'SEARCH_HIGHLIGHT',
  'MAP_FEATURED',
  'MAP_PINNED',
]);

/**
 * Placement types for sidebar sections
 */
export const SIDEBAR_PLACEMENT_TYPES: Set<FeaturedPlacementType> = new Set([
  'SIDEBAR_FEATURED',
  'SIDEBAR_RECENT',
  'SIDEBAR_PREMIUM',
  'WIDGET_FEATURED',
  'WIDGET_TRENDING',
]);

/**
 * Check if a placement type belongs to the Hero section
 */
export function isHeroPlacement(placementType: string | undefined): boolean {
  if (!placementType) return false;
  return HERO_PLACEMENT_TYPES.has(placementType as FeaturedPlacementType);
}

/**
 * Check if a placement type belongs to the Homepage Featured section
 * (Featured Properties carousel, Homepage Grid, Homepage Slider, etc.)
 */
export function isHomepagePlacement(placementType: string | undefined): boolean {
  if (!placementType) return false;
  return HOMEPAGE_PLACEMENT_TYPES.has(placementType as FeaturedPlacementType);
}

/**
 * Check if a placement type belongs to Category sections
 */
export function isCategoryPlacement(placementType: string | undefined): boolean {
  if (!placementType) return false;
  return CATEGORY_PLACEMENT_TYPES.has(placementType as FeaturedPlacementType);
}

/**
 * Check if a placement type is for search boosting
 */
export function isSearchPlacement(placementType: string | undefined): boolean {
  if (!placementType) return false;
  return SEARCH_PLACEMENT_TYPES.has(placementType as FeaturedPlacementType);
}

/**
 * Check if a placement type is for sidebar
 */
export function isSidebarPlacement(placementType: string | undefined): boolean {
  if (!placementType) return false;
  return SIDEBAR_PLACEMENT_TYPES.has(placementType as FeaturedPlacementType);
}

// =============================================================================
// PROPERTY FILTERING HELPERS
// =============================================================================

/**
 * Property with optional featured placements
 */
interface PropertyWithFeatured {
  id?: string;
  name?: string;
  featuredPlacements?: FeaturedPlacementSummary[];
}

/**
 * Filter properties to only those featured for the Homepage section.
 * 
 * @param properties - Array of properties with featuredPlacements
 * @param maxCount - Maximum number of properties to return
 * @returns Filtered and sorted properties for homepage display
 */
export function getHomepageFeaturedProperties<T extends PropertyWithFeatured>(
  properties: T[],
  maxCount: number = 6
): T[] {
  // Filter to properties with homepage placements
  const homepageFeatured = properties.filter(p =>
    p.featuredPlacements?.some(fp => isHomepagePlacement(fp.placementType))
  );
  
  // Sort by priority (highest first)
  const sorted = homepageFeatured.sort((a, b) => {
    const getPriority = (prop: PropertyWithFeatured) => {
      if (!prop.featuredPlacements) return 0;
      const homepagePlacement = prop.featuredPlacements.find(fp => 
        isHomepagePlacement(fp.placementType)
      );
      return homepagePlacement?.priority || 0;
    };
    return getPriority(b) - getPriority(a);
  });
  
  return sorted.slice(0, maxCount);
}

/**
 * Filter properties to only those featured for the Hero section.
 * 
 * @param properties - Array of properties with featuredPlacements
 * @param maxCount - Maximum number of properties to return
 * @returns Filtered and sorted properties for hero display
 */
export function getHeroFeaturedProperties<T extends PropertyWithFeatured>(
  properties: T[],
  maxCount: number = 5
): T[] {
  // Filter to properties with hero placements
  const heroFeatured = properties.filter(p =>
    p.featuredPlacements?.some(fp => isHeroPlacement(fp.placementType))
  );
  
  // Sort by priority (highest first)
  const sorted = heroFeatured.sort((a, b) => {
    const getPriority = (prop: PropertyWithFeatured) => {
      if (!prop.featuredPlacements) return 0;
      const heroPlacement = prop.featuredPlacements.find(fp => 
        isHeroPlacement(fp.placementType)
      );
      return heroPlacement?.priority || 0;
    };
    return getPriority(b) - getPriority(a);
  });
  
  return sorted.slice(0, maxCount);
}
